/**
 * Section heading deduplication logic.
 *
 * The agent's markdown output may contain headings that duplicate the
 * scaffold, belong as sub-headings, or drift into other sections.
 *
 * Rules:
 *   - Matches parent section number exactly → skip (duplicate of scaffold)
 *   - Is a child of parent (starts with parent + ".") → keep as sub-heading
 *   - Is a different section entirely → stop processing (boundary crossed)
 *
 * ONLY explicit markdown heading syntax (## / ###) is treated as a
 * section boundary. Plain text that happens to start with numbers
 * (e.g. "1. Cardio Imaging System", "3.1 billion patients", "Figure
 * 4.1 shows...") is NEVER treated as a heading. This eliminates an
 * entire class of false-positive truncation bugs.
 *
 * The agent is instructed to use ## / ### for headings. If it doesn't,
 * that's an instruction issue to fix upstream, not a parser heuristic
 * to guess at.
 *
 * SOLID:
 *   SRP — only classifies lines as heading/non-heading.
 *   OCP — adding new heading syntax = add one more regex check.
 */

// Match ONLY explicit markdown heading lines:
//   ## 9.2 Title           (CSR / CER / PSUR — pure numeric)
//   ### 9.2.1 Title
//   ## 2. Scope
//   ## 3.2.R.1.1 Title     (CMC — alphabetic codes after the leading digit)
//   ### 3.2.S.4.2 Title
// The section number must start with a digit; subsequent dotted
// segments may be alphanumeric (CMC uses letter codes S/P/A/R for
// drug substance / product / appendices / regional). Section number
// may be followed by a period, space, or both before the title.
const RE_MD_HEADING = /^(#{1,3})\s+(\d+(?:\.[\dA-Za-z]+)*)[\s.]+(.+)$/;

// A drafter-named heading for a placeholder section: ``[HEADING 4.5.1.1: Bleeding
// Complications]`` on a line of its own. The server keeps only the markers a
// module may write (its compiled ``retitle`` list). Mirrors
// ``section_split._RE_HEADING_TITLE``.
export const RE_HEADING_TITLE_MARKER = /^[ \t]*\[HEADING[ \t]+([^\]:\s]+)[ \t]*:[ \t]*([^\]\n]*?)[ \t]*\][ \t]*$/;

/** ``{section number: title}`` from the ``[HEADING n: title]`` lines in *text*. */
export function parseHeadingTitles(text: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const line of (text || "").split("\n")) {
    const m = RE_HEADING_TITLE_MARKER.exec(line);
    const title = m ? m[2].split(/\s+/).filter(Boolean).join(" ") : "";
    if (m && title) out.set(m[1], title);
  }
  return out;
}

/**
 * Classify a markdown heading line relative to a parent section.
 *
 * @param line - The raw markdown line
 * @param parentSectionNumber - The section this content belongs to (e.g. "9.2")
 * @returns
 *   - "skip" if the heading duplicates the parent
 *   - "keep" if it's a valid sub-heading
 *   - "stop" if it belongs to a different section
 *   - "not_heading" if the line isn't a markdown heading
 */
export function classifyHeading(line: string, parentSectionNumber: string): "skip" | "keep" | "stop" | "not_heading" {
  const trimmed = line.trim();
  const match = RE_MD_HEADING.exec(trimmed);

  // Only explicit markdown headings (## / ###) are section boundaries.
  // Everything else is content — no guessing, no heuristics.
  if (!match) {
    return "not_heading";
  }

  const secNum = match[2];

  // Exact match → duplicate of the scaffold heading
  if (secNum === parentSectionNumber) return "skip";

  // Child section (e.g. "9.2.1" under "9.2") → valid sub-heading
  if (secNum.startsWith(`${parentSectionNumber}.`)) return "keep";

  // Different section entirely → content boundary crossed
  return "stop";
}

/**
 * Filter an array of markdown lines, applying heading deduplication.
 *
 * - Skips lines that duplicate the parent heading
 * - Keeps child sub-headings
 * - Drops ``[HEADING n: title]`` markers (see {@link RE_HEADING_TITLE_MARKER})
 * - Demotes a heading that belongs to a DIFFERENT numbered section to
 *   an unnumbered ``**Title**`` bold subtitle (defense in depth — the
 *   server sanitizer should already have demoted these, but if one
 *   leaks through we'd rather keep the prose than truncate the rest of
 *   the draft).
 *
 * @returns The filtered lines (never shorter than input minus duplicates)
 */
export function filterContentLines(
	lines: string[],
	parentSectionNumber: string,
	// Reserved: the section title, passed by some callers (e.g. the web build)
	// for title-aware de-duplication. Currently unused — the numbered-heading
	// logic below already covers the observed duplication cases — so existing
	// two-arg callers are unaffected.
	_title?: string,
): string[] {
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Heading-name markers set the Word heading; they are never body text.
    if (RE_HEADING_TITLE_MARKER.test(line)) continue;
    const classification = classifyHeading(line, parentSectionNumber);

    switch (classification) {
      case "skip":
        continue;

      case "stop": {
        // Stray ``## 6.1 …`` inside a Section-5 draft, or similar.
        // Demote to bold so the title survives but no fake numbered
        // section heading gets written to Word — the parent draft is
        // not the authoritative owner of that section number.
        const trimmed = line.trim();
        const m = RE_MD_HEADING.exec(trimmed);
        const title = m ? (m[3] || "").trim() : "";
        if (title) {
          console.warn(
            `[sectionHeaderLogic] Demoting cross-section heading at line ${i}: ` +
              `"${trimmed.slice(0, 60)}" → "**${title}**" ` +
              `(parent: ${parentSectionNumber})`,
          );
          result.push(`**${title}**`);
        } else {
          console.warn(
            `[sectionHeaderLogic] Dropping title-less cross-section heading at line ${i}: ` +
              `"${trimmed.slice(0, 60)}" (parent: ${parentSectionNumber})`,
          );
        }
        break;
      }

      case "keep":
      case "not_heading":
        result.push(line);
        break;
    }
  }

  return result;
}
